import { reportingPrisma as prisma, OrderItemStatus } from "client";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";
import { getEventTicketOids } from "./getEventTicketOids";

/**
 * Daily ticket sales — counts order items (tickets), not orders.
 * See countingRules.ts for definitions of sold vs pending.
 */
export const getProductsSalesMetrics = async ({
  eventId,
  filters,
  ticketOids: ticketOidsArg,
}: {
  eventId: string;
  filters: { [key: string]: string };
  /** Prefetch via getEventTicketOids to avoid a second Ticket lookup. */
  ticketOids?: Array<{ $oid: string }>;
}) => {
  try {
    const ticketOids = ticketOidsArg ?? (await getEventTicketOids(eventId));
    if (ticketOids.length === 0) return [];

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dateMatchStages: object[] = [];
    if (filters.fromDate && filters.toDate) {
      const fromIso = new Date(filters.fromDate).toISOString();
      const toIso = new Date(filters.toDate).toISOString();
      dateMatchStages.push({
        $match: {
          $expr: {
            $and: [
              {
                $gte: [{ $toDate: "$order.createdAt" }, { $toDate: fromIso }],
              },
              {
                $lte: [{ $toDate: "$order.createdAt" }, { $toDate: toIso }],
              },
            ],
          },
        },
      });
    }

    const stages = [
      {
        $match: {
          status: OrderItemStatus.CONFIRMED,
          ticketId: { $in: ticketOids },
        },
      },
      {
        $lookup: {
          from: "Ticket",
          localField: "ticketId",
          foreignField: "_id",
          as: "ticket",
        },
      },
      { $unwind: "$ticket" },
      {
        // Re-check soft-delete at aggregate time (ids may have been deleted after prefetch).
        $match: {
          $or: [
            { "ticket.deletedAt": null },
            { "ticket.deletedAt": { $exists: false } },
          ],
        },
      },
      {
        $lookup: {
          from: "BookableItem",
          localField: "ticket.bookableItemId",
          foreignField: "_id",
          as: "bookableItem",
        },
      },
      { $unwind: { path: "$bookableItem", preserveNullAndEmptyArrays: true } },
      {
        $lookup: {
          from: "Order",
          localField: "orderId",
          foreignField: "_id",
          as: "order",
        },
      },
      { $unwind: "$order" },
      ...dateMatchStages,
      {
        $lookup: {
          from: "Payment",
          localField: "order._id",
          foreignField: "orderId",
          as: "payments",
        },
      },
      {
        $addFields: {
          today: {
            $dateToString: {
              format: "%Y/%m/%d",
              date: "$order.createdAt",
              timezone: userTimeZone,
            },
          },
          paymentStatuses: {
            $map: {
              input: "$payments",
              as: "p",
              in: "$$p.status",
            },
          },
          orderItemTotal: "$total",
          bookablePrice: { $ifNull: ["$bookableItem.price", 0] },
          isFreeTicket: {
            $cond: {
              if: { $ne: ["$total", null] },
              then: { $lte: ["$total", 0] },
              else: { $lte: ["$bookablePrice", 0] },
            },
          },
          isPaidTicket: {
            $cond: {
              if: { $ne: ["$total", null] },
              then: { $gt: ["$total", 0] },
              else: { $gt: ["$bookablePrice", 0] },
            },
          },
        },
      },
      {
        $addFields: {
          ...buildPaymentSettlementExpressions("$paymentStatuses"),
        },
      },
      {
        $group: {
          _id: "$today",
          "Number of Tickets/Registrations Paid": {
            $sum: {
              $cond: {
                if: {
                  $or: [
                    {
                      $and: [
                        { $eq: ["$anyPaid", true] },
                        { $eq: ["$isPaidTicket", true] },
                      ],
                    },
                    { $eq: ["$isFreeTicket", true] },
                  ],
                },
                then: 1,
                else: 0,
              },
            },
          },
          "Number of Tickets/Registrations Pending Payments": {
            $sum: {
              $cond: {
                if: {
                  $and: [
                    { $eq: ["$hasActiveAwaitingPayment", true] },
                    { $eq: ["$anyPaid", false] },
                    { $eq: ["$isPaidTicket", true] },
                  ],
                },
                then: 1,
                else: 0,
              },
            },
          },
        },
      },
      { $sort: { _id: 1 } },
    ];

    const result = await prisma.orderItem.aggregateRaw({
      pipeline: stages,
    });
    return result || [];
  } catch (error) {
    console.error(error);
    throw error;
  }
};
